import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Dialog,
  Field,
  Icon,
  Input,
  Metric,
  SectionTabs,
  Table,
  viLabel,
  type Column,
} from '@/components/primitives';
import { PageHeader } from '@/components/layout/PageHeader';
import { CalibrationBanner } from '@/components/common/CalibrationBanner';
import { useViewport } from '@/lib/viewport';
import { useAuth } from '@/lib/auth';
import { useCurrentConfig, useConfigHistory } from '@/hooks/useConfig';
import { configService } from '@/services/configService';
import { auditService } from '@/services/auditService';
import type { ConfigValue, EventType } from '@/domain';

const MONTH_LABELS = [
  'T1',
  'T2',
  'T3',
  'T4',
  'T5',
  'T6',
  'T7',
  'T8',
  'T9',
  'T10',
  'T11',
  'T12',
];

const EVENT_ORDER: EventType[] = [
  'NORMAL',
  'PAYDAY',
  'MID_MONTH',
  'MINI_SPIKE',
  'DOUBLE_DAY',
  'MEGA_SALE',
];

const EVENT_HINT: Record<EventType, string> = {
  NORMAL: 'Ngày thường — không có sự kiện',
  PAYDAY: 'Ngày lương — 25 hàng tháng',
  MID_MONTH: 'Giữa tháng — 15 hàng tháng',
  MINI_SPIKE: 'Sale nhỏ — 8/3, 20/10…',
  DOUBLE_DAY: 'Double day — 5.5, 6.6, 7.7, 8.8',
  MEGA_SALE: 'Mega sale — 9.9, 10.10, 11.11, 12.12',
};

type Section = 'monthly' | 'event' | 'formula';

interface EditState {
  key: string;
  label: string;
  value: number;
  kind: ConfigValue['kind'];
  refKey: { kind: 'MONTHLY_MULTIPLIER'; month: number } | { kind: 'EVENT_MULTIPLIER'; event_type: EventType };
}

export function SeasonsConfigPage() {
  const { isMobile, vw } = useViewport();
  const { user } = useAuth();
  const [section, setSection] = useState<Section>('monthly');
  const [editing, setEditing] = useState<EditState | null>(null);

  const monthly = useCurrentConfig('MONTHLY_MULTIPLIER');
  const events = useCurrentConfig('EVENT_MULTIPLIER');

  const monthlyByMonth = useMemo(() => {
    const m = new Map<number, ConfigValue>();
    monthly.forEach((v) => {
      const month = Number(v.key.split(':')[1]);
      if (!Number.isNaN(month)) m.set(month, v);
    });
    return m;
  }, [monthly]);

  const eventByType = useMemo(() => {
    const m = new Map<EventType, ConfigValue>();
    events.forEach((v) => {
      const evt = v.key.split(':')[1] as EventType;
      if (evt) m.set(evt, v);
    });
    return m;
  }, [events]);

  const monthValues = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        month: i + 1,
        label: MONTH_LABELS[i]!,
        value: monthlyByMonth.get(i + 1)?.value ?? 1.0,
        version: monthlyByMonth.get(i + 1)?.version ?? 1,
        config: monthlyByMonth.get(i + 1),
      })),
    [monthlyByMonth],
  );

  const eventValues = useMemo(
    () =>
      EVENT_ORDER.map((evt) => ({
        event_type: evt,
        label: viLabel(evt),
        value: eventByType.get(evt)?.value ?? 1.0,
        version: eventByType.get(evt)?.version ?? 1,
        config: eventByType.get(evt),
      })),
    [eventByType],
  );

  const avgFactor = monthValues.length
    ? (monthValues.reduce((s, m) => s + m.value, 0) / 12).toFixed(2)
    : '—';
  const peakMonth = monthValues.reduce((max, m) => (m.value > max.value ? m : max), monthValues[0]!);
  const lowMonth = monthValues.reduce((min, m) => (m.value < min.value ? m : min), monthValues[0]!);
  const peakEvent = eventValues.reduce(
    (max, e) => (e.value > max.value ? e : max),
    eventValues[0]!,
  );

  async function saveEdit() {
    if (!editing || !user) return;
    await configService.setValue({
      key: editing.refKey,
      value: editing.value,
      note: '',
      actor: user.email,
    });
    await auditService.log({
      actor: user.email,
      action: 'CONFIG_CHANGE',
      entity_type: 'config_value',
      entity_id: editing.key,
      summary: `Cập nhật ${editing.label} thành ×${editing.value.toFixed(2)}`,
      payload: { key: editing.key, value: editing.value },
    });
    setEditing(null);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <PageHeader
        breadcrumb={['Cấu hình', 'Tham số vận hành', 'Tham số']}
        title="Tham số mùa vụ & sự kiện"
        subtitle="Hệ số nhân các tháng trong năm và các loại sự kiện. Áp dụng vào engine forecast để tính khối lượng công việc."
        actions={
          <>
            <Button variant="outline" icon="download">
              Xuất Excel
            </Button>
            <Button variant="outline" icon="history">
              Lịch sử
            </Button>
          </>
        }
      />

      <div style={{ flex: 1, padding: isMobile ? 14 : 20 }}>
        <CalibrationBanner
          subtitle="Monthly factor: T2=0.83 (Tết dip) · T11=1.55 (11.11 peak) · T12=1.45 (year-end). Event multiplier: MEGA_SALE=×3.80 · DOUBLE_DAY=×2.00 · MID_MONTH=×1.10."
        />
        <div
          style={{
            display: 'grid',
            gap: 12,
            marginBottom: 16,
            gridTemplateColumns: vw < 720 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          }}
        >
          <Metric label="Hệ số TB năm" value={`×${avgFactor}`} sublabel="so với baseline 1.00" />
          <Metric
            label="Đỉnh cao nhất"
            value={`×${peakMonth.value.toFixed(2)}`}
            sublabel={peakMonth.label}
            deltaTone="up"
            delta={peakMonth.value > 1.2 ? 'Cao mùa' : 'Bình thường'}
          />
          <Metric
            label="Thấp nhất"
            value={`×${lowMonth.value.toFixed(2)}`}
            sublabel={lowMonth.label}
            deltaTone="down"
            delta={lowMonth.value < 0.9 ? 'Mùa thấp' : 'Bình thường'}
          />
          <Metric
            label="Sự kiện lớn nhất"
            value={`×${peakEvent.value.toFixed(2)}`}
            sublabel={peakEvent.label}
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <SectionTabs
            value={section}
            onChange={(s) => setSection(s as Section)}
            tabs={[
              { id: 'monthly', label: 'Hệ số tháng', idx: 1, count: 12 },
              { id: 'event', label: 'Sự kiện', idx: 2, count: eventValues.length },
              { id: 'formula', label: 'Cách áp dụng', idx: 3 },
            ]}
          />
        </div>

        {section === 'monthly' && (
          <>
            <MonthlyChart monthValues={monthValues} />
            <div style={{ height: 16 }} />
            <MonthlyTable
              monthValues={monthValues}
              onEdit={(m) =>
                setEditing({
                  key: `monthly:${m.month}`,
                  label: m.label + ' · hệ số tháng',
                  value: m.value,
                  kind: 'MONTHLY_MULTIPLIER',
                  refKey: { kind: 'MONTHLY_MULTIPLIER', month: m.month },
                })
              }
            />
          </>
        )}

        {section === 'event' && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: vw < 720 ? '1fr' : 'repeat(2, 1fr)',
              gap: 12,
            }}
          >
            {eventValues.map((e) => (
              <Card key={e.event_type}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginBottom: 6,
                      }}
                    >
                      <Badge
                        kind={
                          e.event_type === 'NORMAL'
                            ? 'DRAFT'
                            : e.event_type === 'MEGA_SALE'
                              ? 'CRITICAL'
                              : 'WARNING'
                        }
                      >
                        {e.label}
                      </Badge>
                      <span
                        style={{
                          fontSize: 11,
                          color: 'var(--bx-text-muted)',
                          fontFamily: 'var(--bx-font-mono)',
                        }}
                      >
                        v{e.version}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: 'var(--bx-text-muted)',
                        lineHeight: 1.5,
                      }}
                    >
                      {EVENT_HINT[e.event_type]}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div
                      style={{
                        fontSize: 28,
                        fontWeight: 700,
                        color: 'var(--bx-text-heading)',
                        fontVariantNumeric: 'tabular-nums',
                        letterSpacing: '-0.02em',
                        lineHeight: 1,
                      }}
                    >
                      ×{e.value.toFixed(2)}
                    </div>
                    <Button
                      variant="ghost-primary"
                      size="sm"
                      icon="edit"
                      onClick={() =>
                        setEditing({
                          key: `event:${e.event_type}`,
                          label: e.label + ' · hệ số sự kiện',
                          value: e.value,
                          kind: 'EVENT_MULTIPLIER',
                          refKey: { kind: 'EVENT_MULTIPLIER', event_type: e.event_type },
                        })
                      }
                    >
                      Sửa
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {section === 'formula' && <FormulaCard />}
      </div>

      <EditDialog editing={editing} setEditing={setEditing} onSave={saveEdit} />
    </div>
  );
}

function MonthlyChart({
  monthValues,
}: {
  monthValues: { month: number; label: string; value: number }[];
}) {
  const max = Math.max(...monthValues.map((m) => m.value));
  const H = 200;
  const todayMonth = new Date().getMonth() + 1;

  return (
    <Card
      title="Đường cong mùa vụ"
      subtitle="Hệ số nhân của 12 tháng so với baseline 1.00 — bar shading by intensity"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 6,
          height: H + 36,
          padding: '0 4px',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 36,
            right: 4,
            bottom: 36,
          }}
        >
          {[0, 0.5, 1, 1.5, 2].filter((g) => g <= max + 0.1).map((g) => (
            <div
              key={g}
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: (g / max) * H,
                borderTop:
                  g === 1 ? '1px dashed var(--bx-primary)' : '1px dashed var(--bx-border-muted)',
                opacity: g === 1 ? 0.6 : 0.5,
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  left: -32,
                  top: -7,
                  fontSize: 10,
                  color: g === 1 ? 'var(--bx-primary)' : 'var(--bx-text-muted)',
                  fontFamily: 'var(--bx-font-mono)',
                  fontWeight: g === 1 ? 600 : 400,
                }}
              >
                ×{g.toFixed(1)}
              </span>
            </div>
          ))}
        </div>
        <div style={{ width: 36 }} />
        {monthValues.map((m) => {
          const h = (m.value / max) * H;
          const isPeak = m.value >= 1.25;
          const isLow = m.value < 0.9;
          const tone = isPeak ? '#b91c1c' : isLow ? '#92400e' : '#0b43ea';
          const isCurrent = m.month === todayMonth;
          return (
            <div
              key={m.month}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4,
                position: 'relative',
              }}
            >
              <div
                style={{
                  fontSize: 10.5,
                  fontFamily: 'var(--bx-font-mono)',
                  fontWeight: 700,
                  color: tone,
                }}
              >
                ×{m.value.toFixed(2)}
              </div>
              <div
                style={{
                  width: '100%',
                  maxWidth: 48,
                  height: h,
                  background: isPeak
                    ? 'linear-gradient(180deg, #fecaca 0%, #fee2e2 100%)'
                    : isLow
                      ? 'linear-gradient(180deg, #fde68a 0%, #fefce8 100%)'
                      : 'linear-gradient(180deg, #c7d2fe 0%, #eef2ff 100%)',
                  border: `1.5px solid ${tone}`,
                  borderRadius: 6,
                  position: 'relative',
                }}
              >
                {isCurrent && (
                  <div
                    style={{
                      position: 'absolute',
                      top: -8,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 8,
                      height: 8,
                      borderRadius: 9999,
                      background: 'var(--bx-primary)',
                      border: '2px solid #fff',
                    }}
                  />
                )}
              </div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: isCurrent ? 700 : 500,
                  color: isCurrent ? 'var(--bx-primary)' : 'var(--bx-text-body)',
                }}
              >
                {m.label}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function MonthlyTable({
  monthValues,
  onEdit,
}: {
  monthValues: { month: number; label: string; value: number; version: number }[];
  onEdit: (m: { month: number; label: string; value: number }) => void;
}) {
  const cols: Column<(typeof monthValues)[number]>[] = [
    {
      header: 'Tháng',
      width: 70,
      render: (r) => (
        <span style={{ fontFamily: 'var(--bx-font-mono)', fontWeight: 600 }}>{r.label}</span>
      ),
    },
    {
      header: 'Hệ số',
      mono: true,
      align: 'right',
      width: 100,
      render: (r) => '×' + r.value.toFixed(2),
    },
    {
      header: 'Mô tả',
      priority: 2,
      render: (r) => {
        if (r.value >= 1.25) return 'Mùa cao điểm';
        if (r.value < 0.9) return 'Mùa thấp';
        if (r.month === new Date().getMonth() + 1) return 'Tháng hiện tại';
        return 'Bình thường';
      },
    },
    {
      header: 'Phiên bản',
      align: 'right',
      mono: true,
      priority: 2,
      render: (r) => `v${r.version}`,
    },
    {
      header: '',
      width: 80,
      render: (r) => (
        <div style={{ textAlign: 'right' }}>
          <Button variant="ghost-primary" size="sm" icon="edit" onClick={() => onEdit(r)}>
            Sửa
          </Button>
        </div>
      ),
    },
  ];
  return (
    <Card title="Bảng hệ số tháng" subtitle="Mọi thay đổi đều tạo phiên bản mới — không ghi đè" padding={0}>
      <Table columns={cols} rows={monthValues} getRowKey={(r) => r.month} />
    </Card>
  );
}

function FormulaCard() {
  return (
    <Card title="Công thức áp dụng" subtitle="Engine forecast M4 sẽ dùng các tham số này">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 13 }}>
        <div
          style={{
            padding: 14,
            background: 'var(--bx-surface-2)',
            borderRadius: 10,
            fontFamily: 'var(--bx-font-mono)',
            lineHeight: 1.6,
          }}
        >
          <div style={{ color: 'var(--bx-text-muted)', marginBottom: 6 }}>
            // Forecast quantity per (warehouse, customer, date)
          </div>
          <div>
            <span style={{ color: 'var(--bx-primary)' }}>forecast_qty</span> = baseline_avg ×{' '}
            <span style={{ color: '#b91c1c' }}>monthly[month]</span> ×{' '}
            <span style={{ color: '#b91c1c' }}>event[event_type]</span>
          </div>
        </div>
        <div
          style={{
            padding: 14,
            background: 'var(--bx-surface-2)',
            borderRadius: 10,
            fontFamily: 'var(--bx-font-mono)',
            lineHeight: 1.6,
          }}
        >
          <div style={{ color: 'var(--bx-text-muted)', marginBottom: 6 }}>
            // Workload minutes per day
          </div>
          <div>
            <span style={{ color: 'var(--bx-primary)' }}>wlu_per_order_min</span> = 60 / UPH_eff
          </div>
          <div>
            <span style={{ color: 'var(--bx-primary)' }}>total_workload_min</span> =
            forecast_qty × wlu_per_order_min × monthly × event
          </div>
        </div>
        <ul
          style={{
            margin: 0,
            paddingLeft: 18,
            color: 'var(--bx-text-muted)',
            lineHeight: 1.6,
          }}
        >
          <li>
            <strong>baseline_avg</strong>: trung bình 90 ngày lịch sử cùng day-type
          </li>
          <li>
            <strong>UPH_eff</strong>: derived từ product_mix khách hàng × UPH norm kho
          </li>
          <li>Versioned — mọi rev đều ghi audit_log</li>
        </ul>
      </div>
    </Card>
  );
}

function EditDialog({
  editing,
  setEditing,
  onSave,
}: {
  editing: EditState | null;
  setEditing: (e: EditState | null) => void;
  onSave: () => void;
}) {
  const history = useConfigHistory(editing?.key ?? '');
  if (!editing) return null;
  return (
    <Dialog
      open={!!editing}
      onClose={() => setEditing(null)}
      title={'Chỉnh sửa ' + editing.label}
      subtitle="Tạo phiên bản mới — bản cũ giữ lại trong lịch sử"
      width={520}
      footer={
        <>
          <Button variant="outline" onClick={() => setEditing(null)}>
            Hủy
          </Button>
          <Button variant="primary" icon="check" onClick={onSave}>
            Tạo phiên bản mới
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Hệ số mới" hint="Số thập phân — ví dụ 1.25 cho tăng 25%">
          <Input
            type="number"
            value={editing.value}
            onChange={(e) =>
              setEditing({ ...editing, value: Number(e.target.value) || 0 })
            }
          />
        </Field>
        {history.length > 0 && (
          <div>
            <div
              style={{
                fontSize: 11,
                color: 'var(--bx-text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontWeight: 700,
                marginBottom: 8,
              }}
            >
              Lịch sử phiên bản
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                maxHeight: 200,
                overflowY: 'auto',
              }}
            >
              {history.map((v) => (
                <div
                  key={v.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 10px',
                    background: 'var(--bx-surface-2)',
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'var(--bx-font-mono)',
                        fontWeight: 600,
                      }}
                    >
                      v{v.version}
                    </span>
                    {v.is_current && (
                      <Badge size="sm" kind="APPROVED">
                        Đang dùng
                      </Badge>
                    )}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      color: 'var(--bx-text-muted)',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'var(--bx-font-mono)',
                        color: 'var(--bx-text-dark)',
                      }}
                    >
                      ×{v.value.toFixed(2)}
                    </span>
                    <Icon name="user" size={11} />
                    {v.created_by}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}
